// ============================================================================
// Shared floorplan helpers
//
// The property model exposes individual units in the `floorplans` collection.
// Both FloorplansList and TabbedCallouts present these grouped by bedroom count
// (Studio / 1 Bedroom / 2 Bedroom / …), so the grouping logic lives here and is
// imported by both to keep one convention across the Home page.
// ============================================================================

export interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

export interface BedroomCategory {
  key: string;
  label: string;
  subtitle: string;
  /** Query param used for deep-linking to the floor plans page (e.g. ?bedroom=one). */
  param: string;
  count: number;
  priceRange: string;
  /** Index into the source floorplans array of this group's representative plan. */
  imageIndex: number;
  /** Representative floor plan image URL for this group (may be null). */
  imageUrl: string | null;
}

export const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

export function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

export function keyToLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bedroom',
    two: '2 Bedroom',
    three: '3 Bedroom',
    four: '4 Bedroom',
    five: '5 Bedroom',
  };
  return labels[key] || `${key} Bedroom`;
}

export function keyToShortLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bed',
    two: '2 Bed',
    three: '3 Bed',
    four: '4 Bed',
    five: '5 Bed',
  };
  return labels[key] || `${key} Bed`;
}

export function formatPriceRange(plans: Floorplan[]): string {
  const prices = plans
    .flatMap((p) => [p.floorplan_price_min, p.floorplan_price_max])
    .map((v) => (v ? parseInt(String(v), 10) : NaN))
    .filter((v) => !isNaN(v) && v > 0);
  if (!prices.length) return 'Call for Pricing';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return `From $${min.toLocaleString()}`;
  return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
}

export function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const groups: Record<string, { plans: Floorplan[]; indices: number[] }> = {};

  for (let i = 0; i < floorplans.length; i++) {
    const plan = floorplans[i];
    const key = bedCountToKey(plan.floorplan_beds_min);
    if (!groups[key]) groups[key] = { plans: [], indices: [] };
    groups[key].plans.push(plan);
    groups[key].indices.push(i);
  }

  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sorted.map((key) => {
    const { plans, indices } = groups[key];
    const count = plans.reduce((sum, p) => {
      const units = p.floorplan_available_units ? parseInt(String(p.floorplan_available_units), 10) : 0;
      return sum + (isNaN(units) ? 0 : units);
    }, 0);

    // Representative image: first plan in the group that actually has an image,
    // falling back to the group's first plan/index.
    let imageIndex = indices[0] ?? 0;
    let imageUrl: string | null = plans[0]?.floorplan_image_url ?? null;
    for (let j = 0; j < plans.length; j++) {
      if (plans[j].floorplan_image_url) {
        imageIndex = indices[j];
        imageUrl = plans[j].floorplan_image_url ?? null;
        break;
      }
    }

    return {
      key,
      label: keyToLabel(key),
      subtitle: keyToShortLabel(key),
      param: key,
      count,
      priceRange: formatPriceRange(plans),
      imageIndex,
      imageUrl,
    };
  });
}
